import { useState } from "react"
import { Link } from "@tanstack/react-router"
import {
  ArrowRight,
  CalendarDays,
  Check,
  ClipboardCheck,
  FileText,
  Loader2,
  Sparkles,
} from "lucide-react"
import { motion } from "motion/react"
import { Button } from "@/components/ui/button"
import { authClient } from "@/lib/auth-client"
import { startProCheckout } from "@/lib/billing/checkout-client"
import { PRO_FREE_TRIAL_DAYS, PRO_PRICE_YEARLY } from "@/lib/billing/constants"
import { trackEvent } from "@/components/Analytics"
import type { LucideIcon } from "lucide-react"

// The Pro feature showcase — each row pairs a real in-app screenshot
// (public/screenshots/pro-*.png, captured via e2e/showcase) with its copy. The
// two export-only features (calendar + AI context) are file downloads with no
// screen of their own, so they share the editor's export menu in one row.
type ProFeatureCard = {
  key: string
  icon: LucideIcon
  title: string
  tagline: string
  body: string
  bullets: readonly string[]
  image: string
  alt: string
}

const FEATURES: readonly ProFeatureCard[] = [
  {
    key: `chicReview`,
    icon: ClipboardCheck,
    title: `ChicReview`,
    tagline: `Your trip, audited`,
    body: `Chic reads your whole itinerary and flags what's missing before you leave — unbooked stays, gaps with no plan, documents you forgot, and prep tasks worth doing now.`,
    bullets: [
      `Spots missing reservations or bookings`,
      `Days with nothing planned yet`,
      `Visa, document, and packing reminders`,
    ],
    image: `/screenshots/pro-chic-review.png`,
    alt: `ChicReview audit listing missing bookings and prep tasks for a Greece trip`,
  },
  {
    key: `pdfExport`,
    icon: FileText,
    title: `PDF Export`,
    tagline: `A printable trip packet`,
    body: `Turn any trip into a polished, shareable PDF — full day-by-day itinerary, place details, and supported attachments — ready to print or hand to a travel companion.`,
    bullets: [
      `Clean day-by-day layout`,
      `Place details and notes included`,
      `Great for offline or sharing`,
    ],
    image: `/screenshots/pro-pdf-print.png`,
    alt: `A printable PDF cover page for a Greece trip with the day-by-day itinerary`,
  },
  {
    key: `exports`,
    icon: CalendarDays,
    title: `Calendar & AI-Context Export`,
    tagline: `Take your trip anywhere`,
    body: `Two one-click exports straight from the trip menu: a calendar (.ics) file that drops your dated itinerary into Google Calendar, Apple Calendar, or Outlook — and the full AI context Chic uses, ready to inspect or hand to any other assistant.`,
    bullets: [
      `Calendar export — dated items become real events`,
      `Works in Google, Apple & Outlook calendars`,
      `AI context export for use with any AI tool`,
    ],
    image: `/screenshots/pro-export-menu.png`,
    alt: `The trip export menu showing Download PDF, Export Calendar, and Export AI Context options`,
  },
]

const INCLUDED = [
  `ChicReview trip audits`,
  `PDF trip exports`,
  `Calendar (.ics) exports`,
  `AI context exports`,
  `Everything in the free plan`,
  `Cancel anytime`,
]

// CTA shared by the hero and pricing card. Logged-in travelers go straight to
// Stripe Checkout; anonymous visitors are routed to sign-up first (the checkout
// endpoint requires a session, so sending them there would just 401).
function UpgradeCta({
  size = `lg`,
  label = `Start ${PRO_FREE_TRIAL_DAYS}-day free trial`,
  source,
}: {
  size?: `lg` | `default`
  label?: string
  source: string
}) {
  const { data: authSession, isPending } = authClient.useSession()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const className =
    size === `lg`
      ? `rounded-full px-8 h-12 text-base font-semibold shadow-md gap-2`
      : `rounded-full px-6 font-semibold gap-2`

  if (!authSession?.user) {
    return (
      <Button size={size} className={className} disabled={isPending} asChild>
        <Link to="/sign-up">
          {label} <ArrowRight className="h-4 w-4" />
        </Link>
      </Button>
    )
  }

  async function handleUpgrade() {
    setLoading(true)
    setError(null)
    trackEvent(`checkout_started`, { source })
    try {
      window.location.href = await startProCheckout()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to start checkout`)
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <Button
        size={size}
        className={className}
        onClick={handleUpgrade}
        disabled={loading}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
        {label}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

export function ProPage() {
  return (
    <main className="min-h-screen bg-background">
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-12 md:pt-16 md:pb-16">
        <motion.div
          className="flex flex-col items-center text-center gap-5 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="inline-flex items-center gap-2 w-fit rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            ChicTravel Pro
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.08] tracking-tight">
            Plan like a pro,
            <br />
            <span className="text-primary">leave nothing to chance.</span>
          </h1>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Pro unlocks the finishing tools — automated trip audits, polished
            PDF packets, calendar exports, and AI context you can take anywhere.
            Everything you need to turn a plan into a trip that runs smoothly.
          </p>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <UpgradeCta source="pro_page_hero" />
            <Button
              size="lg"
              variant="outline"
              className="rounded-full px-8 h-12 text-base font-medium"
              asChild
            >
              <a href="#features">See what's included</a>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">
              ${PRO_PRICE_YEARLY.toFixed(2)}/year
            </span>
            {` `}· {PRO_FREE_TRIAL_DAYS}-day free trial · cancel anytime
          </p>
        </motion.div>
      </section>

      {/* ── Features ──────────────────────────────────────────────────── */}
      <section
        id="features"
        className="px-6 py-14 md:py-16 bg-card border-y border-border scroll-mt-20"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              What's included
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              The tools that finish the job
            </h2>
          </div>

          <div className="flex flex-col gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.key}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center rounded-3xl border border-border bg-background p-6 md:p-8"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: `-60px` }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Real in-app screenshot — leads on alternating sides at lg+ */}
                <div
                  className={`h-56 sm:h-72 lg:h-80 overflow-hidden rounded-2xl border border-border bg-muted shadow-sm ${
                    i % 2 === 1 ? `lg:order-2` : ``
                  }`}
                >
                  <img
                    src={f.image}
                    alt={f.alt}
                    loading="lazy"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className={i % 2 === 1 ? `lg:order-1` : ``}>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary mb-3">
                    <f.icon className="h-4 w-4" />
                    {f.tagline}
                  </div>
                  <h3 className="text-2xl font-extrabold tracking-tight mb-2">
                    {f.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {f.body}
                  </p>
                  <ul className="flex flex-col gap-2">
                    {f.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Check className="h-3 w-3" />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ───────────────────────────────────────────────────── */}
      <section id="pricing" className="px-6 py-16 md:py-20 scroll-mt-20">
        <motion.div
          className="max-w-md mx-auto rounded-3xl border border-border bg-card shadow-xl overflow-hidden"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: `-60px` }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="bg-primary px-8 py-6 text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary-foreground">
              <Sparkles className="h-3.5 w-3.5" />
              Pro
            </span>
            <div className="mt-4 flex items-baseline justify-center gap-1 text-primary-foreground">
              <span className="text-4xl font-extrabold">
                ${PRO_PRICE_YEARLY.toFixed(2)}
              </span>
              <span className="text-sm font-medium opacity-80">/ year</span>
            </div>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Starts with a {PRO_FREE_TRIAL_DAYS}-day free trial
            </p>
          </div>
          <div className="px-8 py-6">
            <ul className="flex flex-col gap-3 mb-6">
              {INCLUDED.map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Check className="h-3 w-3" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <div className="[&_button]:w-full [&>div]:w-full">
              <UpgradeCta source="pro_page_pricing" />
            </div>
            <p className="mt-3 text-center text-xs text-muted-foreground">
              Cancel anytime — no charge during the trial.
            </p>
          </div>
        </motion.div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-primary">
        <motion.div
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: `-60px` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-primary-foreground mb-4">
            Ready to upgrade your next trip?
          </h2>
          <p className="text-primary-foreground/80 text-base mb-10">
            Try every Pro tool free for {PRO_FREE_TRIAL_DAYS} days. Keep what
            you love, cancel if it's not for you.
          </p>
          <div className="flex justify-center [&_button]:bg-white [&_button]:text-primary [&_button:hover]:bg-white/90">
            <UpgradeCta source="pro_page_footer" />
          </div>
        </motion.div>
      </section>
    </main>
  )
}
